/* Holistic Vitalis Educational Advertorial — Experience Engine v3
   HyperFrames-inspired: scene composition, scrubbed reveals, pinned theaters */

(function () {
  'use strict';

  // ── GSAP registration ──────────────────────────────────────────────────────
  gsap.registerPlugin(ScrollTrigger);

  // ── Lenis smooth scroll ────────────────────────────────────────────────────
  const lenis = new Lenis({
    duration: 1.15,
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    orientation: 'vertical',
    smoothWheel: true,
  });

  // Drive Lenis via GSAP ticker (not native RAF) so ScrollTrigger gets clean positions
  lenis.on('scroll', ScrollTrigger.update);
  gsap.ticker.add((time) => { lenis.raf(time * 1000); });
  gsap.ticker.lagSmoothing(0);

  // ── Reading progress bar ───────────────────────────────────────────────────
  gsap.to('#progress-bar', {
    scaleX: 1,
    ease: 'none',
    scrollTrigger: {
      trigger: 'body',
      start: 'top top',
      end: 'bottom bottom',
      scrub: 0.4,
    }
  });

  // ── Nav reveal on scroll ───────────────────────────────────────────────────
  const hvNav = document.querySelector('.hv-nav');
  gsap.to('.hv-nav', {
    y: 0,
    duration: 0.6,
    ease: 'power2.out',
    scrollTrigger: {
      trigger: '#hero',
      start: 'bottom 80%',
      toggleActions: 'play none none reverse',
    }
  });

  // Nav shadow when page is scrolled
  ScrollTrigger.create({
    start: 'top top-=80',
    onEnter: () => hvNav && hvNav.classList.add('scrolled'),
    onLeaveBack: () => hvNav && hvNav.classList.remove('scrolled'),
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // SCENE 1 — HERO ENTRANCE SEQUENCE
  // ═══════════════════════════════════════════════════════════════════════════

  const heroTL = gsap.timeline({ delay: 0.2 });

  // Orbs drift in
  heroTL.fromTo('.hero-orb-1',
    { scale: 0.7, opacity: 0 },
    { scale: 1, opacity: 0.4, duration: 2.2, ease: 'power2.out' }, 0
  );
  heroTL.fromTo('.hero-orb-2',
    { scale: 0.5, opacity: 0 },
    { scale: 1, opacity: 0.3, duration: 2.8, ease: 'power2.out' }, 0.15
  );

  // Product image — cinematic reveal from the right
  heroTL.fromTo('.hero-product-img',
    { opacity: 0, y: 40, scale: 0.92, filter: 'brightness(0.6)' },
    { opacity: 1, y: 0, scale: 1, filter: 'brightness(1)', duration: 1.4, ease: 'power3.out' }, 0.4
  );

  // Pill badge
  heroTL.fromTo('.hero-pill',
    { opacity: 0, y: -16 },
    { opacity: 1, y: 0, duration: 0.55, ease: 'back.out(2)' }, 0.5
  );

  // H1 word-by-word stagger
  const h1 = document.querySelector('#hero h1');
  if (h1) {
    h1.style.opacity = '1'; // un-hide container; word clips handle the reveal
    const words = h1.textContent.trim().split(/\s+/);
    h1.innerHTML = words.map(w => `<span class="h1-word" style="display:inline-block;overflow:hidden;"><span class="h1-inner" style="display:inline-block;">${w}</span></span>`).join(' ');
    heroTL.fromTo(h1.querySelectorAll('.h1-inner'),
      { yPercent: 110, opacity: 0 },
      { yPercent: 0, opacity: 1, stagger: 0.05, duration: 0.75, ease: 'power3.out' }, 0.65
    );
  }

  // Quick answer card — clip-path reveal
  heroTL.fromTo('.quick-answer',
    { opacity: 0, y: 24, clipPath: 'inset(0 0 100% 0)' },
    { opacity: 1, y: 0, clipPath: 'inset(0 0 0% 0)', duration: 0.8, ease: 'power3.out' }, 1.1
  );

  // CTA row
  heroTL.fromTo('.hero-cta-row',
    { opacity: 0, y: 20 },
    { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out' }, 1.35
  );

  // Scroll indicator
  heroTL.fromTo('.scroll-indicator',
    { opacity: 0 },
    { opacity: 1, duration: 0.5 }, 1.65
  );

  // Orb parallax on scroll
  gsap.to('.hero-orb-1', {
    y: -140, rotation: 15,
    scrollTrigger: { trigger: '#hero', start: 'top top', end: 'bottom top', scrub: 1 }
  });
  gsap.to('.hero-orb-2', {
    y: -90, rotation: -10,
    scrollTrigger: { trigger: '#hero', start: 'top top', end: 'bottom top', scrub: 1.5 }
  });

  // Orb idle x-oscillation — x only to avoid conflict with scroll y/rotation parallax
  gsap.to('.hero-orb-1', { x: 26, duration: 4.2, ease: 'sine.inOut', repeat: -1, yoyo: true, overwrite: false });
  gsap.to('.hero-orb-2', { x: -20, duration: 5.8, ease: 'sine.inOut', repeat: -1, yoyo: true, delay: 1.5, overwrite: false });

  // Product column idle float — on wrapper to avoid conflicting with img scroll parallax
  gsap.to('.hero-product-col', { y: -20, duration: 3.4, ease: 'sine.inOut', repeat: -1, yoyo: true, delay: 1.8 });

  // Product image float + fade on scroll out
  gsap.to('.hero-product-img', {
    y: -70, scale: 1.04, opacity: 0.6,
    scrollTrigger: { trigger: '#hero', start: 'top top', end: 'bottom top', scrub: 1.2 }
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // SCENE 2 — TRUST BAR (stat counters)
  // ═══════════════════════════════════════════════════════════════════════════

  const trustBar = document.querySelector('.trust-bar');
  if (trustBar) {
    const trustStats = trustBar.querySelectorAll('.trust-stat');

    // Entrance stagger — use `to` since CSS sets opacity:0 initial state
    gsap.to(trustStats, {
      opacity: 1,
      y: 0,
      stagger: 0.12,
      duration: 0.7,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: trustBar,
        start: 'top 85%',
        toggleActions: 'play none none none',
      }
    });

    // Counter animation per stat
    trustStats.forEach((stat) => {
      const numEl = stat.querySelector('.trust-number');
      if (!numEl) return;

      const rawText = numEl.textContent.trim();
      const suffix = rawText.replace(/[\d,]/g, '');
      const numericStr = rawText.replace(/[^\d]/g, '');
      const target = parseInt(numericStr, 10);
      if (isNaN(target)) return;

      const obj = { val: 0 };
      gsap.to(obj, {
        val: target,
        duration: 2.2,
        ease: 'power2.out',
        onUpdate: function () {
          numEl.textContent = Math.round(obj.val).toLocaleString() + suffix;
        },
        scrollTrigger: {
          trigger: stat,
          start: 'top 85%',
          toggleActions: 'play none none none',
        }
      });
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SCENE 3 — CHAPTER NAVIGATION (floating dot TOC)
  // ═══════════════════════════════════════════════════════════════════════════

  const chapterNav = document.querySelector('.chapter-nav');
  const article = document.querySelector('.article-body');

  if (chapterNav && article) {
    const headings = article.querySelectorAll('h2');

    if (headings.length >= 3) {
      // Build dots
      headings.forEach((h2, i) => {
        // Ensure h2 has an id for scroll targeting
        if (!h2.id) {
          h2.id = 'section-' + i;
        }
        const dot = document.createElement('a');
        dot.className = 'chapter-dot';
        dot.href = '#' + h2.id;
        dot.setAttribute('aria-label', h2.textContent.trim());
        dot.setAttribute('data-label', h2.textContent.trim().slice(0, 28));
        dot.addEventListener('click', (e) => {
          e.preventDefault();
          lenis.scrollTo(h2, { offset: -80, duration: 1.4 });
        });
        chapterNav.appendChild(dot);
      });

      // Show chapter nav after hero
      ScrollTrigger.create({
        trigger: article,
        start: 'top 70%',
        onEnter: () => gsap.to(chapterNav, { opacity: 1, x: 0, duration: 0.5, ease: 'power2.out' }),
        onLeaveBack: () => gsap.to(chapterNav, { opacity: 0, x: 16, duration: 0.3 }),
      });

      // Active dot tracking
      const dots = chapterNav.querySelectorAll('.chapter-dot');
      headings.forEach((h2, i) => {
        ScrollTrigger.create({
          trigger: h2,
          start: 'top 50%',
          end: i < headings.length - 1 ? 'top 50%-=1px' : 'bottom bottom',
          onEnter: () => {
            dots.forEach(d => d.classList.remove('active'));
            if (dots[i]) dots[i].classList.add('active');
          },
          onEnterBack: () => {
            dots.forEach(d => d.classList.remove('active'));
            if (dots[i]) dots[i].classList.add('active');
          },
        });
      });
    } else {
      // Not enough sections — hide nav
      chapterNav.style.display = 'none';
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SCENE 4 — MINERAL THEATER (horizontal scroll showcase)
  // ═══════════════════════════════════════════════════════════════════════════

  const mineralTheater = document.querySelector('.mineral-theater');

  if (mineralTheater) {
    const track = mineralTheater.querySelector('.mineral-track');
    const scenes = mineralTheater.querySelectorAll('.mineral-scene');

    if (track && scenes.length) {

      // Size theater: use container width so scenes fill the visible area exactly
      function sizeTheater() {
        const TW = mineralTheater.offsetWidth; // container width (stays in column)
        const TH = Math.round(TW * 0.72);      // 4:3-ish, shorter than viewport
        mineralTheater.style.height = TH + 'px';
        track.style.width = (scenes.length * TW) + 'px';
        scenes.forEach(s => {
          s.style.width = TW + 'px';
          s.style.flexShrink = '0';
          s.style.height = TH + 'px';
        });
      }
      sizeTheater();

      // Track which scenes have been revealed (opacity: 0 in CSS → must use fromTo)
      const revealed = new Set();

      function revealScene(i) {
        if (revealed.has(i)) return;
        revealed.add(i);
        const scene = scenes[i];
        const specs = [
          { sel: '.ms-number',   from: { opacity: 0, x: -24, y: 0 },    to: { opacity: 1, x: 0, y: 0 } },
          { sel: '.ms-icon',     from: { opacity: 0, scale: 0.5, y: 0 }, to: { opacity: 1, scale: 1, y: 0 } },
          { sel: '.ms-name',     from: { opacity: 0, y: 30 },             to: { opacity: 1, y: 0 } },
          { sel: '.ms-function', from: { opacity: 0, y: 20 },             to: { opacity: 1, y: 0 } },
          { sel: '.ms-desc',     from: { opacity: 0, y: 16 },             to: { opacity: 1, y: 0 } },
          { sel: '.ms-fact',     from: { opacity: 0, scale: 0.8 },        to: { opacity: 1, scale: 1 } },
        ];
        specs.forEach(({ sel, from, to }, j) => {
          const el = scene.querySelector(sel);
          if (el) gsap.fromTo(el, from, { ...to, duration: 0.5, ease: 'power2.out', delay: j * 0.08 });
        });
      }

      // Progress dots
      const dots = mineralTheater.querySelectorAll('.mineral-progress-dot');
      function updateDots(idx) {
        dots.forEach((d, i) => d.classList.toggle('active', i === idx));
      }

      // Reveal first scene when theater scrolls into view (before pin kicks in)
      ScrollTrigger.create({
        trigger: mineralTheater,
        start: 'top 80%',
        onEnter: () => { revealScene(0); updateDots(0); },
      });

      // Horizontal scroll — reveal subsequent scenes as user scrolls through
      gsap.to(track, {
        x: () => -((scenes.length - 1) * mineralTheater.offsetWidth),
        ease: 'none',
        scrollTrigger: {
          trigger: mineralTheater,
          pin: true,
          scrub: 1,
          start: 'top top',
          end: () => '+=' + ((scenes.length - 1) * mineralTheater.offsetWidth),
          invalidateOnRefresh: true,
          onRefresh: () => sizeTheater(),
          onUpdate: (self) => {
            const idx = Math.min(Math.floor(self.progress * scenes.length), scenes.length - 1);
            // Reveal all scenes up to current index (handles fast scrollers)
            for (let i = 0; i <= idx; i++) revealScene(i);
            updateDots(idx);
          },
        }
      });
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SCENE 5 — ARTICLE SECTION REVEALS (clip-path scene wipes)
  // ═══════════════════════════════════════════════════════════════════════════

  if (article) {
    // H2 headings — horizontal wipe reveal
    article.querySelectorAll('h2').forEach((h2) => {
      gsap.fromTo(h2,
        { clipPath: 'inset(0 100% 0 0)', opacity: 0 },
        {
          clipPath: 'inset(0 0% 0 0)', opacity: 1,
          duration: 0.9, ease: 'power3.out',
          scrollTrigger: { trigger: h2, start: 'top 86%', toggleActions: 'play none none none' }
        }
      );
    });

    // Paragraphs — subtle fade lift, trigger early so no blank gaps
    article.querySelectorAll('p').forEach((p) => {
      gsap.fromTo(p,
        { opacity: 0, y: 12 },
        {
          opacity: 1, y: 0,
          duration: 0.55, ease: 'power2.out',
          scrollTrigger: { trigger: p, start: 'top 98%', toggleActions: 'play none none none' }
        }
      );
    });

    // Lists
    article.querySelectorAll('ul, ol').forEach((list) => {
      const items = list.querySelectorAll('li');
      gsap.from(items, {
        opacity: 0, x: -20,
        stagger: 0.07,
        duration: 0.55,
        ease: 'power2.out',
        scrollTrigger: { trigger: list, start: 'top 88%', toggleActions: 'play none none none' }
      });
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SCENE 6 — MINERAL CARDS (grid layout fallback / non-theater pages)
  // ═══════════════════════════════════════════════════════════════════════════

  document.querySelectorAll('.mineral-card').forEach((card) => {
    // Skip if inside theater (handled above)
    if (card.closest('.mineral-theater')) return;

    gsap.fromTo(card,
      { opacity: 0, y: 48, scale: 0.96 },
      {
        opacity: 1, y: 0, scale: 1,
        duration: 0.85, ease: 'power3.out',
        scrollTrigger: { trigger: card, start: 'top 87%' }
      }
    );

    // List items inside
    const items = card.querySelectorAll('.mineral-list li');
    if (items.length) {
      gsap.from(items, {
        opacity: 0, x: -16,
        stagger: 0.1,
        duration: 0.5,
        ease: 'power2.out',
        scrollTrigger: { trigger: card, start: 'top 82%' }
      });
    }
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // SCENE 7 — TIMELINE (results timeline)
  // ═══════════════════════════════════════════════════════════════════════════

  const timelineItems = document.querySelectorAll('.timeline-item');
  const timelineFill  = document.querySelector('.timeline-track-fill');

  if (timelineItems.length && timelineFill) {
    timelineItems.forEach((item, i) => {
      gsap.fromTo(item,
        { opacity: 0, x: i % 2 === 0 ? -40 : 40 },
        {
          opacity: 1, x: 0,
          duration: 0.7, ease: 'power2.out',
          scrollTrigger: {
            trigger: item,
            start: 'top 84%',
            onEnter: () => {
              const dot = item.querySelector('.timeline-dot');
              if (dot) dot.classList.add('active');
              const pct = ((i + 1) / timelineItems.length) * 100;
              gsap.to(timelineFill, { height: pct + '%', duration: 0.7, ease: 'power2.out' });
            }
          }
        }
      );
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SCENE 8 — PRODUCT CARDS (advertorial placement)
  // ═══════════════════════════════════════════════════════════════════════════

  document.querySelectorAll('.product-card').forEach((card) => {
    gsap.fromTo(card,
      { opacity: 0, y: 60, scale: 0.94 },
      {
        opacity: 1, y: 0, scale: 1,
        duration: 1.0, ease: 'power3.out',
        scrollTrigger: { trigger: card, start: 'top 86%' }
      }
    );

    // 3D tilt on hover
    card.addEventListener('mousemove', (e) => {
      const rect = card.getBoundingClientRect();
      const cx = rect.left + rect.width  / 2;
      const cy = rect.top  + rect.height / 2;
      const dx = (e.clientX - cx) / (rect.width  / 2);
      const dy = (e.clientY - cy) / (rect.height / 2);
      gsap.to(card, { rotateX: -dy * 4, rotateY: dx * 4, duration: 0.35, ease: 'power1.out' });
    });
    card.addEventListener('mouseleave', () => {
      gsap.to(card, { rotateX: 0, rotateY: 0, duration: 0.7, ease: 'elastic.out(1, 0.5)' });
    });
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // SCENE 9 — FAQ ACCORDION
  // ═══════════════════════════════════════════════════════════════════════════

  const faqItems = document.querySelectorAll('.faq-item');

  faqItems.forEach((item, i) => {
    gsap.fromTo(item,
      { opacity: 0, y: 24 },
      {
        opacity: 1, y: 0,
        duration: 0.6, ease: 'power2.out',
        delay: i * 0.07,
        scrollTrigger: {
          trigger: item.closest('.faq-section') || item,
          start: 'top 82%',
        }
      }
    );

    const btn    = item.querySelector('.faq-question');
    const answer = item.querySelector('.faq-answer');
    if (!btn || !answer) return;

    btn.addEventListener('click', () => {
      const isOpen = item.classList.contains('open');

      // Close all others
      faqItems.forEach((other) => {
        if (other !== item && other.classList.contains('open')) {
          other.classList.remove('open');
          gsap.to(other.querySelector('.faq-answer'), { height: 0, duration: 0.35, ease: 'power2.inOut' });
        }
      });

      if (isOpen) {
        item.classList.remove('open');
        gsap.to(answer, { height: 0, duration: 0.35, ease: 'power2.inOut' });
      } else {
        item.classList.add('open');
        gsap.set(answer, { height: 'auto' });
        const h = answer.scrollHeight;
        gsap.fromTo(answer,
          { height: 0 },
          { height: h, duration: 0.42, ease: 'power2.out' }
        );
      }
    });
  });

  // Open first FAQ by default
  if (faqItems.length) {
    const first  = faqItems[0];
    const answer = first.querySelector('.faq-answer');
    if (answer) {
      first.classList.add('open');
      gsap.set(answer, { height: 'auto' });
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SCENE 10 — CTA BANNER (final conversion section)
  // ═══════════════════════════════════════════════════════════════════════════

  const ctaBanner = document.querySelector('.cta-banner');
  if (ctaBanner) {
    // Background pulse on enter
    ScrollTrigger.create({
      trigger: ctaBanner,
      start: 'top 70%',
      onEnter: () => {
        gsap.fromTo(ctaBanner,
          { backgroundSize: '100% 100%' },
          { backgroundSize: '110% 110%', duration: 8, ease: 'sine.inOut', yoyo: true, repeat: -1 }
        );
      }
    });

    gsap.from(ctaBanner.querySelectorAll('h2, p, a, .cta-social-proof'), {
      opacity: 0, y: 36,
      stagger: 0.13,
      duration: 0.85,
      ease: 'power3.out',
      scrollTrigger: { trigger: ctaBanner, start: 'top 76%' }
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SCENE 11 — CALLOUT BOXES
  // ═══════════════════════════════════════════════════════════════════════════

  document.querySelectorAll('.callout').forEach((el) => {
    gsap.fromTo(el,
      { opacity: 0, x: -28, borderLeftWidth: '0px' },
      {
        opacity: 1, x: 0, borderLeftWidth: '4px',
        duration: 0.75, ease: 'power2.out',
        scrollTrigger: { trigger: el, start: 'top 87%' }
      }
    );
  });

  // Pull quotes — slide in from left like callouts
  document.querySelectorAll('.pull-quote').forEach((el) => {
    gsap.to(el, {
      opacity: 1, x: 0, duration: 0.8, ease: 'power2.out',
      scrollTrigger: { trigger: el, start: 'top 88%' }
    });
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // SCENE 12 — EDU VIDEO SECTIONS (autoplay on scroll)
  // ═══════════════════════════════════════════════════════════════════════════

  document.querySelectorAll('.edu-video-section').forEach((section) => {
    const video = section.querySelector('video');
    const label = section.querySelector('.edu-video-label');

    // Reveal section
    gsap.fromTo(section,
      { opacity: 0, scale: 0.97 },
      {
        opacity: 1, scale: 1,
        duration: 1.0, ease: 'power3.out',
        scrollTrigger: { trigger: section, start: 'top 80%' }
      }
    );

    if (label) {
      gsap.from(label, {
        opacity: 0, y: 16,
        duration: 0.6, delay: 0.3,
        ease: 'power2.out',
        scrollTrigger: { trigger: section, start: 'top 80%' }
      });
    }

    if (video) {
      ScrollTrigger.create({
        trigger: section,
        start: 'top 65%',
        end: 'bottom 20%',
        onEnter: () => { video.muted = true; video.play().catch(() => {}); },
        onLeave: () => { video.pause(); },
        onEnterBack: () => { video.muted = true; video.play().catch(() => {}); },
        onLeaveBack: () => { video.pause(); },
      });
    }
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // SCENE 13 — DATA-COUNT STANDALONE COUNTERS
  // ═══════════════════════════════════════════════════════════════════════════

  document.querySelectorAll('[data-count]').forEach((el) => {
    // Skip if already handled by trust bar
    if (el.closest('.trust-bar')) return;

    const target = parseInt(el.dataset.count, 10);
    const obj = { val: 0 };
    gsap.to(obj, {
      val: target,
      duration: 2,
      ease: 'power2.out',
      onUpdate: function () {
        el.textContent = Math.round(obj.val).toLocaleString();
      },
      scrollTrigger: { trigger: el, start: 'top 85%' }
    });
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // SCENE 14 — STICKY MOBILE CTA
  // ═══════════════════════════════════════════════════════════════════════════

  (function () {
    const navCta = document.querySelector('.nav-cta');
    if (!navCta) return;

    const el = document.createElement('div');
    el.id = 'sticky-cta';
    el.innerHTML = '<a class="sticky-cta-btn" href="' + navCta.href + '">'
      + navCta.textContent.trim() + '</a>';
    document.body.appendChild(el);

    gsap.set('#sticky-cta', { y: 100 });

    ScrollTrigger.create({
      trigger: '#hero',
      start: 'bottom top',
      onEnter: () => gsap.to('#sticky-cta', { y: 0, duration: 0.5, ease: 'power3.out' }),
      onLeaveBack: () => gsap.to('#sticky-cta', { y: 100, duration: 0.3, ease: 'power2.in' }),
    });
  })();

  // ═══════════════════════════════════════════════════════════════════════════
  // SCENE 15 — SCIENCE STATS ENTRANCE
  // ═══════════════════════════════════════════════════════════════════════════

  // ═══════════════════════════════════════════════════════════════════════════
  // SCENE 16 — BACK TO TOP BUTTON
  // ═══════════════════════════════════════════════════════════════════════════

  (function () {
    const btn = document.createElement('button');
    btn.id = 'back-to-top';
    btn.setAttribute('aria-label', 'Back to top');
    btn.textContent = '↑';
    document.body.appendChild(btn);

    gsap.set('#back-to-top', { opacity: 0, scale: 0.8 });

    ScrollTrigger.create({
      start: 'top top-=400',
      onEnter: () => gsap.to('#back-to-top', { opacity: 1, scale: 1, duration: 0.35, ease: 'back.out(2)' }),
      onLeaveBack: () => gsap.to('#back-to-top', { opacity: 0, scale: 0.8, duration: 0.2 }),
    });

    btn.addEventListener('click', () => {
      lenis.scrollTo(0, { duration: 1.2, easing: t => 1 - Math.pow(1 - t, 4) });
    });
  })();

  // Related pages grid stagger in
  document.querySelectorAll('.related-grid').forEach((grid) => {
    gsap.to(grid.querySelectorAll('.related-card'), {
      opacity: 1, y: 0,
      duration: 0.55, ease: 'power2.out', stagger: 0.08,
      scrollTrigger: { trigger: grid, start: 'top 88%' }
    });
  });

  // Author byline + disclaimer reveals (start at opacity:0 from CSS)
  document.querySelectorAll('.author-byline').forEach((el) => {
    gsap.to(el, {
      opacity: 1, y: 0, duration: 0.7, ease: 'power2.out',
      scrollTrigger: { trigger: el, start: 'top 90%' }
    });
  });

  // Testimonial cards stagger in
  gsap.utils.toArray('.testimonial-strip').forEach((strip) => {
    gsap.to(strip.querySelectorAll('.testimonial-card'), {
      opacity: 1, y: 0,
      duration: 0.65, ease: 'power3.out', stagger: 0.15,
      scrollTrigger: { trigger: strip, start: 'top 85%' }
    });
  });

  gsap.utils.toArray('.science-stats').forEach((block) => {
    gsap.from(block, {
      y: 40, opacity: 0, duration: 0.8, ease: 'power3.out',
      scrollTrigger: { trigger: block, start: 'top 85%' }
    });
    gsap.from(block.querySelectorAll('.science-stat-item'), {
      y: 24, opacity: 0, duration: 0.6, ease: 'power2.out', stagger: 0.15,
      scrollTrigger: { trigger: block, start: 'top 80%' }
    });
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // SCENE 17 — INLINE ARTICLE TABLE OF CONTENTS
  // ═══════════════════════════════════════════════════════════════════════════

  (function () {
    const article = document.querySelector('.article-body');
    if (!article) return;

    const headings = Array.from(article.querySelectorAll('h2'));
    if (headings.length < 3) return;

    // Build TOC element
    const toc = document.createElement('nav');
    toc.className = 'article-toc';
    toc.setAttribute('aria-label', 'Article sections');

    const wordCount = (article.innerText || article.textContent || '').trim().split(/\s+/).length;
    const readMins = Math.max(1, Math.round(wordCount / 200));

    const label = document.createElement('div');
    label.className = 'toc-label';
    label.textContent = 'In this article · ' + readMins + ' min read';
    toc.appendChild(label);

    const list = document.createElement('ol');
    list.className = 'toc-list';

    headings.forEach((h2, i) => {
      if (!h2.id) h2.id = 'section-' + (i + 1);
      const li = document.createElement('li');
      li.className = 'toc-item';
      const a = document.createElement('a');
      a.className = 'toc-link';
      a.href = '#' + h2.id;
      a.textContent = h2.textContent.trim();
      a.addEventListener('click', (e) => {
        e.preventDefault();
        lenis.scrollTo(h2, { offset: -90, duration: 1.2, easing: (t) => 1 - Math.pow(1 - t, 4) });
      });
      li.appendChild(a);
      list.appendChild(li);
    });

    toc.appendChild(list);

    // Insert before first h2
    const firstH2 = article.querySelector('h2');
    if (firstH2) {
      firstH2.insertAdjacentElement('beforebegin', toc);
    } else {
      article.appendChild(toc);
    }

    // Animate in
    gsap.to(toc, {
      opacity: 1, y: 0, duration: 0.7, ease: 'power2.out',
      scrollTrigger: { trigger: toc, start: 'top 88%' },
    });

    // Active section tracking
    const items = list.querySelectorAll('.toc-item');
    headings.forEach((h2, i) => {
      ScrollTrigger.create({
        trigger: h2,
        start: 'top 42%',
        onEnter: () => items.forEach((item, j) => item.classList.toggle('active', j === i)),
        onEnterBack: () => items.forEach((item, j) => item.classList.toggle('active', j === i)),
      });
    });
  })();

  // ═══════════════════════════════════════════════════════════════════════════
  // SCENE 18 — MOBILE MINERAL THEATER SWIPE HINT
  // ═══════════════════════════════════════════════════════════════════════════

  (function () {
    if (window.innerWidth > 600) return;
    const theater = document.querySelector('.mineral-theater');
    if (!theater) return;
    const hint = document.createElement('div');
    hint.className = 'theater-swipe-hint';
    hint.textContent = 'Swipe to explore →';
    theater.appendChild(hint);
    gsap.set(hint, { opacity: 0 });
    ScrollTrigger.create({
      trigger: theater,
      start: 'top 70%',
      once: true,
      onEnter: () => {
        gsap.to(hint, {
          opacity: 1, duration: 0.4, delay: 0.5,
          onComplete: () => gsap.to(hint, { opacity: 0, duration: 0.9, delay: 2.5 }),
        });
      },
    });
  })();

  // ═══════════════════════════════════════════════════════════════════════════
  // SCENE 21 — TESTIMONIAL STAR FILL ANIMATION
  // ═══════════════════════════════════════════════════════════════════════════

  (function () {
    document.querySelectorAll('.testimonial-stars').forEach((el) => {
      const raw = el.textContent.trim();
      el.innerHTML = raw.split('').map(ch => '<span class="star-char">' + ch + '</span>').join('');
      const stars = el.querySelectorAll('.star-char');
      gsap.to(stars, {
        opacity: 1, scale: 1,
        duration: 0.3, ease: 'back.out(2)', stagger: 0.07,
        scrollTrigger: { trigger: el, start: 'top 90%' },
      });
    });
  })();

  // ═══════════════════════════════════════════════════════════════════════════
  // SCENE 20 — GLOBAL LENIS ANCHOR INTERCEPT
  // ═══════════════════════════════════════════════════════════════════════════

  (function () {
    document.addEventListener('click', (e) => {
      const a = e.target.closest('a[href^="#"]');
      if (!a || e.defaultPrevented) return;
      const target = document.querySelector(a.getAttribute('href'));
      if (!target) return;
      e.preventDefault();
      lenis.scrollTo(target, { offset: -80, duration: 1.3, easing: (t) => 1 - Math.pow(1 - t, 4) });
    });
  })();

  // ═══════════════════════════════════════════════════════════════════════════
  // SCENE 19 — PRODUCT CARD TRUST BADGES
  // ═══════════════════════════════════════════════════════════════════════════

  (function () {
    document.querySelectorAll('.product-info').forEach((info) => {
      if (info.querySelector('.product-trust')) return;
      const trust = document.createElement('div');
      trust.className = 'product-trust';
      trust.innerHTML =
        '<span>🔒 Secure checkout</span>' +
        '<span>↩ 30-day guarantee</span>' +
        '<span>📦 Free shipping</span>';
      info.appendChild(trust);
    });
  })();

  // ═══════════════════════════════════════════════════════════════════════════
  // SCENE 22 — SOCIAL SHARE STRIP (drives return traffic via sharing)
  // ═══════════════════════════════════════════════════════════════════════════

  (function () {
    const byline = document.querySelector('.author-byline');
    if (!byline || document.querySelector('.share-strip')) return;

    const pageUrl = encodeURIComponent(window.location.href);
    const pageTitle = encodeURIComponent(document.title);
    const heroImg = document.querySelector('.hero-product-img');
    const imgUrl = heroImg ? encodeURIComponent(heroImg.src) : '';

    const strip = document.createElement('div');
    strip.className = 'share-strip';
    strip.innerHTML =
      '<span class="share-label">Share this guide</span>' +
      '<a class="share-btn share-pinterest" href="https://pinterest.com/pin/create/button/?url=' + pageUrl + '&media=' + imgUrl + '&description=' + pageTitle + '" target="_blank" rel="noopener" aria-label="Save to Pinterest">📌 Pinterest</a>' +
      '<a class="share-btn share-facebook" href="https://www.facebook.com/sharer/sharer.php?u=' + pageUrl + '" target="_blank" rel="noopener" aria-label="Share on Facebook">📘 Facebook</a>' +
      '<a class="share-btn share-whatsapp" href="https://wa.me/?text=' + pageTitle + '%20' + pageUrl + '" target="_blank" rel="noopener" aria-label="Share on WhatsApp">💬 WhatsApp</a>';

    byline.insertAdjacentElement('afterend', strip);

    gsap.fromTo(strip,
      { opacity: 0, y: 16 },
      { opacity: 1, y: 0, duration: 0.7, ease: 'power2.out',
        scrollTrigger: { trigger: strip, start: 'top 92%' } }
    );
  })();

  // ═══════════════════════════════════════════════════════════════════════════
  // UTILITY — ScrollTrigger refresh on images load
  // ═══════════════════════════════════════════════════════════════════════════

  window.addEventListener('load', () => {
    ScrollTrigger.refresh();
  });

})();
